import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Upload, Button, Space, Divider } from 'antd';
import { UploadOutlined, RobotOutlined, LoadingOutlined } from '@ant-design/icons';
import { ImageStyle } from '../../types';
import { imageStylesApi } from '../../services/api/imageStyles';
import { useAsyncOperation } from '../../hooks';

const { TextArea } = Input;

interface EditImageStyleModalProps {
  visible: boolean;
  style: ImageStyle | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditImageStyleModal: React.FC<EditImageStyleModalProps> = ({
  visible,
  style,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { execute, loading } = useAsyncOperation();
  const [extracting, setExtracting] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      if (style) {
        form.setFieldsValue(style);
        setPreviewImage(style.illustrationUrl || null);
      } else {
        form.resetFields();
        form.setFieldsValue({ status: 'ACTIVE' });
        setPreviewImage(null);
      }
    }
  }, [visible, style, form]);

  const handleExtractStyle = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target?.result as string;
      const base64Data = base64.split(',')[1];
      setPreviewImage(base64);

      setExtracting(true);
      try {
        const response = await imageStylesApi.extractImageStyle(base64);
        const { suggestedName, description, illustrationUrl } = response.data;
        
        form.setFieldsValue({
          name: suggestedName,
          description: description,
          illustrationUrl: illustrationUrl || undefined,
        });

        if (illustrationUrl) {
          setPreviewImage(illustrationUrl);
        }

        message.success('Style extracted successfully!');
      } catch (error) {
        message.error('Failed to extract style from image');
      } finally {
        setExtracting(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      await execute(
        async () => {
          if (style) {
            return imageStylesApi.updateImageStyle(style.id, values);
          } else {
            return imageStylesApi.createImageStyle(values);
          }
        },
        {
          successMessage: style ? 'Style updated successfully' : 'Style created successfully',
          errorMessage: style ? 'Failed to update style' : 'Failed to create style',
        }
      );
      
      onSuccess();
    } catch (error) {
      // Form validation error
    }
  };

  return (
    <Modal
      title={style ? 'Edit Image Style' : 'Create Image Style'}
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={700}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ status: 'ACTIVE' }}
      >
        <Form.Item
          name="code"
          label="Style Code"
          rules={[
            { required: true, message: 'Please input style code' },
            { pattern: /^[A-Z0-9_]+$/, message: 'Code must be uppercase, numbers, or underscores' }
          ]}
        >
          <Input placeholder="e.g. ANIME_MODERN" disabled={!!style} />
        </Form.Item>

        <Form.Item
          name="name"
          label="Style Name"
          rules={[{ required: true, message: 'Please input style name' }]}
        >
          <Input placeholder="e.g. Modern Anime" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Artistic Description (Style Prompt)"
          help="Describe the artistic elements like medium, lighting, color palette, brushstrokes..."
          rules={[{ required: true, message: 'Please input style description' }]}
        >
          <TextArea rows={6} placeholder="Describe the artistic style..." />
        </Form.Item>

        <Form.Item
          name="illustrationUrl"
          label="Illustration URL"
        >
          <Input placeholder="https://example.com/image.jpg" />
        </Form.Item>

        <Divider orientation={"left" as any}>AI Style Analysis</Divider>
        
        <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '8px', border: '1px dashed #d9d9d9' }}>
          <Space align="start" size="large">
            <Upload
              beforeUpload={(file) => {
                handleExtractStyle(file);
                return false;
              }}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={extracting ? <LoadingOutlined /> : <UploadOutlined />} loading={extracting}>
                Upload & Analyze Style
              </Button>
            </Upload>

            {previewImage && (
              <div style={{ textAlign: 'center' }}>
                <img 
                  src={previewImage} 
                  alt="Preview" 
                  style={{ maxWidth: 120, maxHeight: 120, borderRadius: 4, display: 'block', marginBottom: 4 }} 
                />
                <span style={{ fontSize: 12, color: '#999' }}>Style Reference</span>
              </div>
            )}
          </Space>
          
          <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
            <RobotOutlined style={{ marginRight: 4 }} />
            Upload an image to let AI automatically suggest a name and describe the artistic style.
          </div>
        </div>

        <Form.Item
          name="status"
          label="Status"
          style={{ marginTop: 24 }}
          rules={[{ required: true }]}
        >
          <Select>
            <Select.Option value="ACTIVE">Active</Select.Option>
            <Select.Option value="INACTIVE">Inactive</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditImageStyleModal;
